import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MergeWithButton } from './MergeWithButton';
import { ShieldCheck, Plus, Pencil, Mail, Hash, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { DEFAULT_CATEGORY_CONFIG } from '../ExpensesPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });
const EMPTY = { name: '', category: 'alimentos', group: 'suministros', aliases: '', nifs: '', sender_emails: '' };
const list = (txt) => txt.split(/[,\n]/).map(x => x.trim()).filter(Boolean);

const VendorDialog = ({ open, initial, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    setF(initial ? { ...initial, aliases: (initial.aliases || []).join(', '), nifs: (initial.nifs || []).join(', '), sender_emails: (initial.sender_emails || []).join(', ') } : EMPTY);
  }, [initial, open]);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    const body = { ...f, aliases: list(f.aliases), nifs: list(f.nifs), sender_emails: list(f.sender_emails) };
    const res = await fetch(`${API_URL}/api/admin/gastos/proveedores/autorizados${initial ? `/${initial.id}` : ''}`,
      { method: initial ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(body) });
    const j = await res.json();
    if (!res.ok) return toast.error(j.detail || 'Error al guardar');
    toast.success(j.moved_expenses ? `Proveedor guardado · ${j.moved_expenses} facturas movidas a su categoría` : 'Proveedor guardado');
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{initial ? 'Editar proveedor autorizado' : 'Nuevo proveedor autorizado'}</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          <Input placeholder="Nombre fiscal" value={f.name} onChange={set('name')} data-testid="auth-vendor-name" />
          <label className="block text-xs text-gray-500">Categoría (todas sus facturas irán aquí)
            <select value={f.category} onChange={set('category')} className="mt-1 w-full h-9 rounded-md border px-2 text-sm text-gray-900" data-testid="auth-vendor-category">
              {Object.entries(DEFAULT_CATEGORY_CONFIG).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
            </select>
          </label>
          <Input placeholder="Otros nombres (separados por coma)" value={f.aliases} onChange={set('aliases')} data-testid="auth-vendor-aliases" />
          <Input placeholder="NIF/CIF (separados por coma)" value={f.nifs} onChange={set('nifs')} data-testid="auth-vendor-nifs" />
          <Input placeholder="Correos remitentes (se aprenden solos)" value={f.sender_emails} onChange={set('sender_emails')} data-testid="auth-vendor-emails" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={!f.name.trim()} data-testid="auth-vendor-save-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const norm = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const matches = (v, q) => !q || [v.name, ...(v.aliases || []), ...(v.nifs || []), ...(v.sender_emails || [])].some(x => norm(x).includes(q));

export const AuthorizedVendors = ({ search = '', onChanged, reloadKey }) => {
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/admin/gastos/proveedores/autorizados`, { headers: headers() })
      .then(r => r.json()).then(j => setVendors(j.vendors || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (v) => {
    if (!window.confirm(`¿Quitar "${v.name}" de la lista autorizada? Sus facturas nuevas irán a "No reconocidos".`)) return;
    await fetch(`${API_URL}/api/admin/gastos/proveedores/autorizados/${v.id}`, { method: 'DELETE', headers: headers() });
    load();
  };

  const q = norm(search.trim());
  const shown = vendors.filter(v => matches(v, q));
  const groups = [['suministros', 'Alimentación, bebidas y suministros'], ['servicios', 'Servicios y gastos operativos']];
  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="authorized-vendors">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <ShieldCheck size={18} className="text-emerald-600" />
        <h3 className="font-semibold text-gray-900 flex-1">Lista autorizada para facturas automáticas ({q ? `${shown.length} de ${vendors.length}` : vendors.length})</h3>
        <Button size="sm" variant="outline" onClick={() => { setEditing(null); setOpen(true); }} data-testid="auth-vendor-add-btn"><Plus size={14} className="mr-1" /> Añadir</Button>
      </div>
      {q && shown.length === 0 && <p className="px-4 py-6 text-center text-sm text-gray-400" data-testid="auth-vendors-empty">Ningún proveedor autorizado coincide con "{search}"</p>}
      {groups.filter(([g]) => shown.some(v => (v.group || 'suministros') === g)).map(([g, label]) => (
        <div key={g}>
          <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
          <div className="divide-y">
            {shown.filter(v => (v.group || 'suministros') === g).map(v => (
              <div key={v.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`auth-vendor-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <Link to={`/admin/gastos/proveedor/${encodeURIComponent(v.name)}`} className="block font-medium text-gray-900 truncate hover:text-blue-700 hover:underline" title="Ver historial, facturas y compras" data-testid={`auth-vendor-open-${v.id}`}>{v.name}</Link>
                  <p className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                    <span>{DEFAULT_CATEGORY_CONFIG[v.category]?.label || v.category}</span>
                    <span className="inline-flex items-center gap-1"><Mail size={11} />{v.sender_emails?.length ? v.sender_emails.join(', ') : 'remitente por aprender'}</span>
                    {v.nifs?.length > 0 && <span className="inline-flex items-center gap-1"><Hash size={11} />{v.nifs.join(', ')}</span>}
                  </p>
                </div>
                <MergeWithButton vendor={v} vendors={vendors} onMerged={() => { load(); onChanged?.(); }} />
                <button onClick={() => { setEditing(v); setOpen(true); }} className="p-2 text-gray-400 hover:text-blue-600" data-testid={`auth-vendor-edit-${v.id}`}><Pencil size={15} /></button>
                <button onClick={() => remove(v)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`auth-vendor-remove-${v.id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
      <VendorDialog open={open} initial={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
};
